// Custom element filter & sort collection/search — render AJAX tanpa reload halaman.

class FacetFiltersForm extends HTMLElement {
  // Inisialisasi form facet: bind handler submit & keyboard escape.
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  // Pasang listener tombol back/forward browser untuk restore filter dari history.
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  // Enable/disable tombol hapus filter aktif saat request AJAX berjalan.
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  // Tampilkan / sembunyikan state loading grid & product count.
  static setLoadingState(isLoading) {
    const gridContainer = document.getElementById('ProductGridContainer');
    const collectionEl = gridContainer?.querySelector('.collection');
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    if (collectionEl) collectionEl.classList.toggle('loading', isLoading);
    if (countContainer) countContainer.classList.toggle('loading', isLoading);
    if (countContainerDesktop) countContainerDesktop.classList.toggle('loading', isLoading);

    loadingSpinners.forEach((spinner) => spinner.classList.toggle('hidden', !isLoading));
  }

  // Render ulang halaman collection berdasarkan searchParams filter (AJAX).
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.renderId = (FacetFiltersForm.renderId || 0) + 1;
    const renderId = FacetFiltersForm.renderId;
    const sections = FacetFiltersForm.getSections();
    if (!sections.length) return;

    FacetFiltersForm.setLoadingState(true);
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    const tagRoutePath = FacetFiltersForm.getTagRoutePath(searchParams);
    const requestPath = tagRoutePath || FacetFiltersForm.getCollectionPath();
    const searchTagParams = tagRoutePath ? null : FacetFiltersForm.getSearchTagRequestParams(searchParams);
    const requestSearchParams = tagRoutePath
      ? FacetFiltersForm.removeSearchParam(searchParams, 'filter.p.tag')
      : searchTagParams || searchParams;

    sections.forEach((section) => {
      const url = `${requestPath}?section_id=${section.section}${requestSearchParams ? `&${requestSearchParams}` : ''}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, renderId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, renderId);
    });
  }

  // Proses HTML section hasil fetch/cache lalu update UI.
  static renderSection(html, event, renderId) {
    if (renderId && renderId !== FacetFiltersForm.renderId) return;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.updateCollectionToolbarState();
    FacetFiltersForm.validateSearchToolbarFilters();
    FacetFiltersForm.setLoadingState(false);
    FacetFiltersForm.toggleActiveFacets(false);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html);
  }

  // Ambil HTML section dari server lalu update filter, grid, dan count.
  static renderSectionFromFetch(url, event, renderId) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: responseText, url }];
        FacetFiltersForm.renderSection(responseText, event, renderId);
      })
      .catch(() => {
        if (renderId && renderId !== FacetFiltersForm.renderId) return;
        FacetFiltersForm.setLoadingState(false);
        FacetFiltersForm.toggleActiveFacets(false);
      });
  }

  // Render section dari cache response fetch sebelumnya (hindari request ulang).
  static renderSectionFromCache(filterDataUrl, event, renderId) {
    const cached = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cached?.html) {
      if (renderId && renderId !== FacetFiltersForm.renderId) return;
      FacetFiltersForm.setLoadingState(false);
      FacetFiltersForm.toggleActiveFacets(false);
      return;
    }

    FacetFiltersForm.renderSection(cached.html, event, renderId);
  }

  // Ganti isi product grid container dengan HTML hasil filter.
  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceContainer = parsedHTML.getElementById('ProductGridContainer');
    const targetContainer = document.getElementById('ProductGridContainer');
    if (!sourceContainer || !targetContainer) return;

    targetContainer.innerHTML = sourceContainer.innerHTML;
    FacetFiltersForm.rehydrateInlineScripts(targetContainer);

    targetContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    // Scroll ke atas area produk setelah filter di-apply
    const filterAnchor = document.getElementById('ProductGridContainer') || document.getElementById('product-grid');
    if (filterAnchor) {
      filterAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (typeof window.reinitInfiniteScroll === 'function') {
      window.reinitInfiniteScroll();
    }

    if (typeof window.reinitCollectionToolbarScroll === 'function') {
      window.reinitCollectionToolbarScroll();
    }

    if (typeof window.reinitCollectionFilterScrollbars === 'function') {
      window.reinitCollectionFilterScrollbars(targetContainer);
    }

  }

  // Jalankan ulang inline script pada HTML hasil render AJAX (termasuk color-swatch snippet).
  static rehydrateInlineScripts(container) {
    const scripts = container.querySelectorAll('script');
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');

      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  // Update teks jumlah produk setelah filter diterapkan.
  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsedHTML.getElementById('ProductCount');
    if (!countEl) return;

    const count = countEl.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) container.innerHTML = count;
    if (containerDesktop) containerDesktop.innerHTML = count;
  }

  // Sync panel filter (desktop, mobile, pills) dengan response server.
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Hapus facet yang tidak lagi dikembalikan server.
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parentFilter = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (parentFilter) parentFilter.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    const activeFilter = event?.target?.closest?.('.js-filter');
    if (countsToRender && activeFilter) {
      FacetFiltersForm.renderCounts(countsToRender, activeFilter);
      FacetFiltersForm.renderMobileCounts(countsToRender, activeFilter);

      const newElementSelector = activeFilter.classList.contains('mobile-facets__details')
        ? `.mobile-facets__close-button`
        : `.facets__summary`;
      const newElementToActivate = activeFilter.querySelector(newElementSelector);
      const isTextInput = event.target.getAttribute('type') === 'text';

      if (newElementToActivate && !isTextInput) newElementToActivate.focus();
    }
  }

  // Update pill/tag filter yang sedang aktif (mobile & desktop).
  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const activeFacetsTarget = document.querySelector(selector);
      if (!activeFacetsElement || !activeFacetsTarget) return;
      activeFacetsTarget.innerHTML = activeFacetsElement.innerHTML;
    });
  }

  // Update elemen tambahan drawer: tombol buka, count mobile, dan sorting.
  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceEl = html.querySelector(selector);
      const targetEl = document.querySelector(selector);
      if (!sourceEl || !targetEl) return;
      targetEl.innerHTML = sourceEl.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm?.closest('menu-drawer');
    if (menuDrawer?.bindEvents) menuDrawer.bindEvents();
  }

  // Update count & isi list filter desktop untuk satu grup filter.
  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  // Update count & list opsi filter di drawer mobile.
  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  // Push URL baru ke browser history tanpa reload halaman.
  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${FacetFiltersForm.getCollectionPath()}${searchParams && '?'.concat(searchParams)}`);
  }

  static getCollectionPath() {
    const segments = window.location.pathname.split('/').filter(Boolean);
    if (segments[segments.length - 1]?.startsWith('shoe_type_')) segments.pop();
    return `/${segments.join('/')}`;
  }

  static handleize(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9_]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  static getTagRoutePath(searchParams) {
    if (!window.location.pathname.startsWith('/collections/')) return null;

    const params = new URLSearchParams(searchParams);
    const tagHandles = params.getAll('filter.p.tag').filter(Boolean).map(FacetFiltersForm.handleize);
    if (!tagHandles.length) return null;
    return `${FacetFiltersForm.getCollectionPath()}/${tagHandles.join('+')}`;
  }

  static getSearchTagRequestParams(searchParams) {
    if (window.location.pathname !== '/search') return null;

    const params = new URLSearchParams(searchParams);
    const tagValues = params.getAll('filter.p.tag').filter(Boolean);
    if (!tagValues.length) return null;

    const q = (params.get('q') || '').replace(/\s*tag:"[^"]*"/g, '').trim();
    const tagQuery = tagValues.map((tagValue) => `tag:"${tagValue.replace(/"/g, '\\"')}"`).join(' ');
    params.delete('filter.p.tag');
    params.set('q', [q, tagQuery].filter(Boolean).join(' '));

    return params.toString();
  }

  static removeSearchParam(searchParams, paramName) {
    const params = new URLSearchParams(searchParams);
    params.delete(paramName);
    return params.toString();
  }

  static updateCollectionToolbarState(searchParams = FacetFiltersForm.searchParamsPrev) {
    const activeTags = new URLSearchParams(searchParams).getAll('filter.p.tag').map(FacetFiltersForm.handleize);

    document.querySelectorAll('.collection-grid-filters__item').forEach((item) => {
      const link = item.querySelector('a.js-collection-filter-link');
      if (!link) return;

      const itemTag = item.dataset.filterTag;
      const linkTags = itemTag ? [itemTag] : new URL(link.href).searchParams.getAll('filter.p.tag');
      const isActive = linkTags.map(FacetFiltersForm.handleize).some((tagValue) => activeTags.includes(tagValue));

      item.classList.toggle('is-active', isActive);
      link.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  static validateSearchToolbarFilters() {
    if (window.location.pathname !== '/search') return;

    const candidates = Array.from(document.querySelectorAll('[data-search-filter-candidate]'));
    if (!candidates.length) return;

    const section = FacetFiltersForm.getSections()[0];
    if (!section?.section) return;

    const cache = new Map();

    candidates.forEach((item) => {
      const link = item.querySelector('a.js-collection-filter-link');
      if (!link) return;

      const linkUrl = new URL(link.href);
      const requestParams = FacetFiltersForm.getSearchTagRequestParams(linkUrl.search.slice(1));
      if (!requestParams) return;

      const requestUrl = `${FacetFiltersForm.getCollectionPath()}?section_id=${section.section}&${requestParams}`;

      const validate = cache.get(requestUrl) || fetch(requestUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          return parsedHTML.querySelectorAll('#product-grid .fc__grid-item').length;
        })
        .catch(() => 0);

      cache.set(requestUrl, validate);

      validate.then((count) => {
        item.hidden = count <= 0;
      });
    });
  }

  // Daftar section Shopify yang perlu di-render ulang saat filter berubah.
  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid?.dataset?.id) return [];

    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  // Ubah data form filter menjadi query string URL.
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  // Trigger render halaman setelah form filter disubmit.
  onSubmitForm(searchParams, event) {
    const currentParams = new URLSearchParams(window.location.search);
    const submittedParams = new URLSearchParams(searchParams);
    
    // Jika form yang disubmit tidak menyertakan input filter.p.tag tetapi URL memilikinya, pertahankan tag tersebut
    if (!submittedParams.has('filter.p.tag') && currentParams.has('filter.p.tag')) {
      currentParams.getAll('filter.p.tag').forEach(function(tag) {
        submittedParams.append('filter.p.tag', tag);
      });
    }

    // Jika form yang disubmit tidak menyertakan input sort_by tetapi URL memilikinya, pertahankan sort_by tersebut
    if (!submittedParams.has('sort_by') && currentParams.has('sort_by')) {
      submittedParams.set('sort_by', currentParams.get('sort_by'));
    }

    FacetFiltersForm.renderPage(submittedParams.toString(), event);
  }

  // Handler submit form filter — gabungkan desktop/mobile/sort lalu render AJAX.
  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  // Handler klik link filter aktif (pill/remove) — legacy, pakai href query string.
  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.validateSearchToolbarFilters();
if (new URLSearchParams(FacetFiltersForm.searchParamsInitial).has('filter.p.tag')) {
  FacetFiltersForm.renderPage(FacetFiltersForm.searchParamsInitial, null, false);
}

class PriceRange extends HTMLElement {
  // Inisialisasi input range harga min/max di filter.
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  // Validasi nilai saat user selesai mengisi input harga.
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  // Batasi keyboard input hanya angka dan tombol navigasi.
  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  // Set atribut data-min/data-max antar input min dan max.
  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) minInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') minInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  // Clamp nilai input agar tidak di bawah min atau di atas max.
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  // Wrapper link hapus/ubah filter aktif di dalam facet form.
  constructor() {
    super();
    this.onFacetLinkClick = this.onFacetLinkClick.bind(this);
  }

  // Pasang event click/keyboard ke link di dalam facet-remove.
  connectedCallback() {
    const facetLink = this.querySelector('a');
    if (!facetLink || facetLink.dataset.facetBound === 'true') return;

    facetLink.dataset.facetBound = 'true';
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.onFacetLinkClick);
    facetLink.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'SPACE') this.onFacetLinkClick(event);
    });
  }

  // Delegasi klik link ke applyFilterLink (AJAX tanpa reload).
  onFacetLinkClick(event) {
    FacetFiltersForm.applyFilterLink(event, event.currentTarget);
  }
}

customElements.define('facet-remove', FacetRemove);

// Terapkan filter dari href link (toolbar shoe type, pill aktif, dll.) via AJAX.
FacetFiltersForm.applyFilterLink = (event, linkEl) => {
  const link = linkEl || event.currentTarget;
  if (!link?.href || link.getAttribute('aria-disabled') === 'true') return;

  const url = new URL(link.href);
  const toolbarItem = link.closest('.collection-grid-filters__item');
  const toolbarTag = toolbarItem?.dataset?.filterTag;
  const isToolbarFilter = link.classList.contains('js-collection-filter-link') && toolbarTag;

  event.preventDefault();
  event.stopPropagation();
  FacetFiltersForm.toggleActiveFacets(true);

  const currentParams = new URLSearchParams(window.location.search);
  const linkParams = new URLSearchParams(url.search);
  const nextParams = new URLSearchParams(currentParams);
  linkParams.forEach((value, key) => {
    if (key !== 'filter.p.tag') nextParams.set(key, value);
  });

  if (window.location.pathname === '/search') {
    nextParams.set('q', (nextParams.get('q') || '').replace(/\s*tag:"[^"]*"/g, '').trim());
  }

  const linkTags = isToolbarFilter ? [toolbarTag] : linkParams.getAll('filter.p.tag');

  if (!isToolbarFilter && !linkTags.length) {
    const searchParams = linkParams.toString();
    FacetFiltersForm.updateCollectionToolbarState(searchParams);
    FacetFiltersForm.renderPage(searchParams, null);
    return;
  }

  if (linkTags.length) {
    const activeToolbarTags = Array.from(document.querySelectorAll('.collection-grid-filters__item.is-active'))
      .map((item) => item.dataset.filterTag)
      .filter(Boolean);
    const currentTags = currentParams.getAll('filter.p.tag');
    const activeTags = currentTags.length ? currentTags : activeToolbarTags;
    const activeTagHandles = activeTags.map(FacetFiltersForm.handleize);
    const isRemovingActiveTag = linkTags
      .map(FacetFiltersForm.handleize)
      .every((tagValue) => activeTagHandles.includes(tagValue));

    if (isRemovingActiveTag) {
      const tagsToKeep = currentTags.filter(function(tagValue) {
        return !linkTags.map(FacetFiltersForm.handleize).includes(FacetFiltersForm.handleize(tagValue));
      });
      nextParams.delete('filter.p.tag');
      tagsToKeep.forEach(function(tagValue) {
        nextParams.append('filter.p.tag', tagValue);
      });
    } else {
      const activeTagHandles = currentTags.map(FacetFiltersForm.handleize);
      linkTags.forEach(function(tagValue) {
        if (!activeTagHandles.includes(FacetFiltersForm.handleize(tagValue))) {
          nextParams.append('filter.p.tag', tagValue);
        }
      });
    }
  }

  const searchParams = nextParams.toString();

  FacetFiltersForm.updateCollectionToolbarState(searchParams);
  FacetFiltersForm.renderPage(searchParams, null);
};

// Delegasi klik filter shoe type di collection toolbar (.js-collection-filter-link).
document.addEventListener('click', (event) => {
  const link = event.target.closest('a.js-collection-filter-link');
  if (!link) return;

  FacetFiltersForm.applyFilterLink(event, link);
});
